import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Lock, Database } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
    tags: ""
  });

  const authToken = localStorage.getItem('admin_token');

  useEffect(() => {
    if (authToken) {
      setIsAuthenticated(true);
      loadKnowledgeItems();
    }
  }, [authToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
        toast.success("تم تسجيل الدخول بنجاح");
        loadKnowledgeItems();
      } else {
        toast.error("اسم المستخدم أو كلمة المرور غير صحيحة");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("حدث خطأ في تسجيل الدخول");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    toast.success("تم تسجيل الخروج");
  };

  const loadKnowledgeItems = async () => {
    try {
      const response = await axios.get(`${API}/knowledge`);
      setKnowledgeItems(response.data);
    } catch (error) {
      console.error("Error loading knowledge items:", error);
      toast.error("حدث خطأ في تحميل البيانات");
    }
  };

  const handleInitializeKnowledge = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/admin/init-knowledge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadKnowledgeItems();
    } catch (error) {
      console.error("Error initializing knowledge:", error);
      toast.error("حدث خطأ في تهيئة قاعدة المعرفة");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('admin_token');
    const data = {
      title: formData.title,
      content: formData.content,
      category: formData.category,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
    };

    try {
      if (editingItem) {
        await axios.put(
          `${API}/knowledge/${editingItem.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("تم تحديث المعلومات بنجاح");
      } else {
        await axios.post(
          `${API}/knowledge`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("تم إضافة المعلومات بنجاح");
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadKnowledgeItems();
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("حدث خطأ في حفظ البيانات");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      content: item.content,
      category: item.category,
      tags: item.tags.join(', ')
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('هل أنت متأكد من الحذف؟')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/knowledge/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم الحذف بنجاح");
      loadKnowledgeItems();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("حدث خطأ في الحذف");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title: "", content: "", category: "", tags: "" });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-500 rounded-2xl flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">لوحة التحكم</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="username">اسم المستخدم</Label>
                <Input
                  id="username"
                  data-testid="admin-username-input"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">كلمة المرور</Label>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  dir="ltr"
                />
              </div>
              <Button type="submit" data-testid="admin-login-button" className="w-full bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600">
                تسجيل الدخول
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">لوحة التحكم</h1>
            <p className="text-gray-600 mt-1">إدارة قاعدة المعرفة</p>
          </div>
          <Button onClick={handleLogout} variant="outline" data-testid="admin-logout-button">
            تسجيل الخروج
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">إجمالي العناصر</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{knowledgeItems.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">الفئات</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {new Set(knowledgeItems.map(item => item.category)).size}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-500 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">تهيئة قاعدة المعرفة</CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleInitializeKnowledge} 
                data-testid="init-knowledge-button"
                variant="secondary" 
                size="sm"
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                <Database className="w-4 h-4 ml-2" />
                تهيئة البيانات الافتراضية
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">قاعدة المعرفة</h2>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                data-testid="add-knowledge-button"
                className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600"
              >
                <Plus className="w-4 h-4 ml-2" />
                إضافة جديد
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" dir="rtl">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'تعديل' : 'إضافة'} عنصر معرفي</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>العنوان</Label>
                  <Input
                    data-testid="knowledge-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>المحتوى</Label>
                  <Textarea
                    data-testid="knowledge-content-input"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={8}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>الفئة</Label>
                    <Input
                      data-testid="knowledge-category-input"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="services, products, about"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>الوسوم (مفصولة بفاصلة)</Label>
                    <Input
                      data-testid="knowledge-tags-input"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="خدمات, تطوير"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    إلغاء
                  </Button>
                  <Button 
                    type="submit" 
                    data-testid="save-knowledge-button"
                    className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600"
                  >
                    حفظ
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4" data-testid="knowledge-items-list">
          {knowledgeItems.map((item) => (
            <Card key={item.id} data-testid={`knowledge-item-${item.id}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.content}</p>
                  </div>
                  <div className="flex gap-2 mr-4">
                    <Button
                      onClick={() => handleEdit(item)}
                      data-testid={`edit-knowledge-${item.id}`}
                      size="sm"
                      variant="outline"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-knowledge-${item.id}`}
                      size="sm"
                      variant="destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 items-center text-xs text-gray-500">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{item.category}</span>
                  {item.tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded">{tag}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
